import { useCallback } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  Checkbox,
  Flex,
  Text,
  Tooltip,
  VisuallyHidden,
} from '@enechain/ecloud-designsystem'

import { AwaitingVoltageLegend } from '~/components/balance/AwaitingVoltageMark'
import { ToggleSwitch } from '~/components/balance/FilterCard/ToggleSwitch'
import {
  DsSelect,
  type DsSelectOption,
  type DsSelectOptions,
  flattenDsSelectOptions,
} from '~/components/common/DsSelect'
import { HelpIcon } from '~/components/common/HelpIcon'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
  type FilterGridItem,
  type ToolbarStatusItem,
} from '~/components/common/filters'
import {
  BALANCE_DEMAND_SERIES_COLORS,
  BALANCE_VOLTAGE_CLASS_COLORS,
} from '~/constants/chartPalette'
import { DisplayDateControl } from '~/pages/BalanceDemand/components/FilterCard/DisplayDateControl'
import type { SeriesKey } from '~/pages/BalanceDemand/constants'
import {
  buildSeriesSummaryItems,
  countChangedSeriesGroups,
  formatSummaryDate,
  getSeriesGroupStates,
} from '~/pages/BalanceDemand/models/filterSummary'
import type { DetailFilterState } from '~/pages/BalanceDemandDetail/constants'
import {
  DEFAULT_DETAIL_FILTER,
  isDemandGroupFilter,
} from '~/pages/BalanceDemandDetail/constants'
import type { BgFilterSelection } from '~/pages/BalanceDemandDetail/hooks/useDemandDetail'
import {
  UNATTRIBUTED_BG_CODE,
  bgOptionLicenseTypeLabel,
  groupBgOptionsByLicenseType,
} from '~/pages/BalanceDemandDetail/models/bgFilterOptions'

/** 開閉の保存キー (画面ごとに一意)。 */
export const BALANCE_DEMAND_DETAIL_FILTER_STORAGE_KEY = 'balanceDemandDetail'

type ToggleItem = {
  key: SeriesKey
  labelKey: string
  /** ON トラック色 (design .switch-item[data-color])。省略 = 既定 accent-primary。 */
  color?: string
}

// ラベルは一覧 (balanceDemand.filter) と同一文言のため i18n key を共用する。
// [design差異] design (`balance/bg-region.html` L112-113) は「基本表示」に
// 太陽光計画値/太陽光実績値の switch-item を今も持つが、データソースが
// 未確定・未配線 (#2002) のため #5282 で画面から削除した (doji-bg.md
// Discussion「太陽光計画値/実績値 系列の扱い」)。復活条件は #2002 の解決と
// セット。design 側の改修は未着手 — 本注記が実装漏れ判定の起点。
const BASIC_TOGGLES: ToggleItem[] = [
  {
    key: 'demandPlan',
    labelKey: 'page:balanceDemand.filter.demandPlan',
    color: BALANCE_DEMAND_SERIES_COLORS.demandPlan,
  },
  {
    key: 'demandActual',
    labelKey: 'page:balanceDemand.filter.demandActual',
    color: BALANCE_DEMAND_SERIES_COLORS.demandActual,
  },
  { key: 'weather', labelKey: 'page:balanceDemand.filter.weather' },
]

const ANALYSIS_TOGGLES: ToggleItem[] = [
  {
    key: 'temperature',
    labelKey: 'page:balanceDemand.filter.temperature',
    color: BALANCE_DEMAND_SERIES_COLORS.temperature,
  },
  {
    key: 'precipitation',
    labelKey: 'page:balanceDemand.filter.precipitation',
    color: BALANCE_DEMAND_SERIES_COLORS.precipitation,
  },
  {
    key: 'procurementNet',
    labelKey: 'page:balanceDemand.filter.procurementNet',
    color: BALANCE_DEMAND_SERIES_COLORS.procurementNet,
  },
]

// 電圧階級別は「詳細」配下から独立した親トグルグループ (doji-bg.md AC-BDB-031)。
const VOLTAGE_TOGGLES: ToggleItem[] = [
  {
    key: 'voltageExtraHigh',
    labelKey: 'page:balanceDemand.filter.voltageExtraHigh',
    color: BALANCE_VOLTAGE_CLASS_COLORS.voltageExtraHigh,
  },
  {
    key: 'voltageHigh',
    labelKey: 'page:balanceDemand.filter.voltageHigh',
    color: BALANCE_VOLTAGE_CLASS_COLORS.voltageHigh,
  },
  {
    key: 'voltageLow',
    labelKey: 'page:balanceDemand.filter.voltageLow',
    color: BALANCE_VOLTAGE_CLASS_COLORS.voltageLow,
  },
]

const DETAIL_TOGGLES: ToggleItem[] = [
  {
    key: 'demandActualCorrected',
    labelKey: 'page:balanceDemand.filter.demandActualCorrected',
    color: BALANCE_DEMAND_SERIES_COLORS.demandActualCorrected,
  },
  {
    key: 'dewPoint',
    labelKey: 'page:balanceDemand.filter.dewPoint',
    color: BALANCE_DEMAND_SERIES_COLORS.dewPoint,
  },
]

type Props = {
  displayDate: Date
  onDateChange: (date: Date) => void
  onToday: () => void
  filter: DetailFilterState
  onFilterChange: (filter: DetailFilterState) => void
  // 選択肢に含まれるかの補正は呼び出し側 (useDemandDetailWithBgSelection) の責務。
  // 必須にしているのは、渡し忘れると黙って「すべて」表示になるため。
  bgFilter: BgFilterSelection
  /**
   * 表示中のコマに低圧未着があるか。 ある時だけ「詳細」 の行の末尾に網掛けの
   * 見本を出す (design `balance/bg-region.html` の `#lv-pending-legend`)。
   */
  hasAwaitingVoltageKoma: boolean
}

export const FilterCard = ({
  displayDate,
  onDateChange,
  onToday,
  filter,
  onFilterChange,
  bgFilter,
  hasAwaitingVoltageKoma,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const bgSelectOptions: DsSelectOptions = [
    { label: t('page:balanceDemandDetail.filter.bgAll'), value: '' },
    ...groupBgOptionsByLicenseType(bgFilter.options).map((group) => ({
      label: bgOptionLicenseTypeLabel(group.licenseType, t),
      options: group.options.map((option) => ({
        label: option.bgName,
        value: option.bgCode,
      })),
    })),
    // 帰属不明は BG ではないので種別の見出しを付けず、区切り線で BG の一覧と分ける
    // (AC-BDB-025-2、#9882 Des 判断)。bg_options には載らない。
    ...(bgFilter.unattributedSelectable
      ? [
          {
            label: '',
            options: [
              {
                label: t('page:balanceDemandDetail.filter.bgUnattributed'),
                value: UNATTRIBUTED_BG_CODE,
              },
            ],
          },
        ]
      : []),
  ]
  const selectedBgLabel =
    bgFilter.selectedCode === ''
      ? undefined
      : flattenDsSelectOptions(bgSelectOptions).find(
          (option) => option.value === bgFilter.selectedCode,
        )?.label

  const retailOptions: DsSelectOption[] = [
    { label: t('page:balanceDemandDetail.filter.retailAll'), value: '' },
    ...bgFilter.retailers.map((retailer) => ({
      label: retailer.businessName,
      value: retailer.businessCode,
    })),
    ...(bgFilter.isRetailerUnattributedSelectable
      ? [
          {
            label: t('page:balanceDemandDetail.filter.bgUnattributed'),
            value: UNATTRIBUTED_BG_CODE,
          },
        ]
      : []),
  ]

  const demandGroupOptions: DsSelectOption[] = [
    {
      label: t('page:balanceDemandDetail.filter.demandGroupAll'),
      value: 'all',
    },
    {
      label: t('page:balanceDemandDetail.filter.demandGroupIndustrial'),
      value: 'grp_industrial',
    },
    {
      label: t('page:balanceDemandDetail.filter.demandGroupCommercial'),
      value: 'grp_commercial',
    },
    {
      label: t('page:balanceDemandDetail.filter.demandGroupResidential'),
      value: 'grp_residential',
    },
  ]

  const handleBgChange = useCallback(
    (bgCode: string | undefined): void => {
      if (bgCode !== undefined) {
        // 別 BG にも同じ小売が居ても「前の BG の小売」が黙って効き続けないよう戻す。
        onFilterChange({ ...filter, bgCode, retailerCode: '' })
      }
    },
    [filter, onFilterChange],
  )

  const handleRetailChange = useCallback(
    (retailerCode: string | undefined): void => {
      if (retailerCode !== undefined) {
        onFilterChange({ ...filter, retailerCode })
      }
    },
    [filter, onFilterChange],
  )

  const handleDemandGroupChange = useCallback(
    (demandGroup: string | undefined): void => {
      if (demandGroup !== undefined && isDemandGroupFilter(demandGroup)) {
        onFilterChange({ ...filter, demandGroup })
      }
    },
    [filter, onFilterChange],
  )

  const toggleSeries = useCallback(
    (key: SeriesKey): void => {
      onFilterChange({
        ...filter,
        series: { ...filter.series, [key]: !filter.series[key] },
      })
    },
    [filter, onFilterChange],
  )

  const renderToggle = (
    item: ToggleItem,
    isDisabled: boolean,
  ): React.ReactElement => (
    <ToggleSwitch
      key={item.key}
      isDisabled={isDisabled}
      isOn={filter.series[item.key]}
      label={t(item.labelKey)}
      onColor={item.color}
      onToggle={() => toggleSeries(item.key)}
    />
  )

  const bgAllLabel = t('page:balanceDemandDetail.filter.bgAll')
  const retailAllLabel = t('page:balanceDemandDetail.filter.retailAll')
  const selectedRetailLabel =
    bgFilter.selectedRetailerCode === ''
      ? undefined
      : retailOptions.find(
          (option) => option.value === bgFilter.selectedRetailerCode,
        )?.label
  const selectedDemandGroupLabel = demandGroupOptions.find(
    (option) => option.value === filter.demandGroup,
  )?.label

  const seriesStates = getSeriesGroupStates(
    filter.series,
    {
      voltage: filter.voltageEnabled,
      analysis: filter.analysisEnabled,
      detail: filter.detailEnabled,
    },
    DEFAULT_DETAIL_FILTER.series,
  )
  // 既定から変えた条件の数 (= 「クリア」で戻る値)。表示日は数えず、戻さない。
  // 系列トグルは群ごとに 1 件
  const modifiedCount =
    [
      bgFilter.selectedCode !== '',
      bgFilter.selectedRetailerCode !== '',
      filter.demandGroup !== DEFAULT_DETAIL_FILTER.demandGroup,
    ].filter(Boolean).length + countChangedSeriesGroups(seriesStates)
  const clearFilters = (): void => {
    onFilterChange({
      ...filter,
      bgCode: DEFAULT_DETAIL_FILTER.bgCode,
      retailerCode: DEFAULT_DETAIL_FILTER.retailerCode,
      demandGroup: DEFAULT_DETAIL_FILTER.demandGroup,
      series: DEFAULT_DETAIL_FILTER.series,
      voltageEnabled: DEFAULT_DETAIL_FILTER.voltageEnabled,
      analysisEnabled: DEFAULT_DETAIL_FILTER.analysisEnabled,
      detailEnabled: DEFAULT_DETAIL_FILTER.detailEnabled,
    })
  }

  const dateLabel = t('page:balanceDemandDetail.filter.displayDate')
  const bgLabel = t('page:balanceDemandDetail.filter.bg')
  const retailLabel = t('page:balanceDemandDetail.filter.retail')
  const demandGroupLabel = t('page:balanceDemandDetail.filter.demandGroup')
  // 閉じた要約。この画面にエリアは無いので表示日が先頭。BG・小売・需要グループは常に今の値
  // (既定は「すべて」/「BG 全体」)、系列の 4 群は既定から変えた群だけ
  const summary: ToolbarStatusItem[] = [
    {
      key: 'date',
      label: dateLabel,
      value: formatSummaryDate(displayDate, t),
    },
    { key: 'bg', label: bgLabel, value: selectedBgLabel ?? bgAllLabel },
    {
      key: 'retail',
      label: retailLabel,
      value: selectedRetailLabel ?? retailAllLabel,
    },
    {
      key: 'demandGroup',
      label: demandGroupLabel,
      value: selectedDemandGroupLabel ?? '',
    },
    ...buildSeriesSummaryItems(seriesStates, t),
  ]

  /** 1 群の部品。群の名前は格子のラベルが出す (親チェックボックスと補足 (?) は部品の先頭)。 */
  const renderGroup = (
    items: readonly ToggleItem[],
    isItemDisabled: (item: ToggleItem) => boolean,
    parent?: {
      group: 'voltageClass' | 'analysis' | 'detail'
      checked: boolean
      onToggle: () => void
    },
    trailing?: React.ReactNode,
  ): React.ReactElement => (
    <Flex alignItems="center" flexWrap="wrap" gap="12px">
      {parent !== undefined && (
        <Flex alignItems="center" gap="4px">
          {/* 群ごとまとめて出し入れするチェックボックス。 ラベルの無いチェックボックスは置かない
              (2026-11-04 デザイナー指摘「label のない checkbox は許容しない、「すべて」などつける」)。
              読み上げは群の名前と合わせて「{群}をすべて表示」 */}
          {/* DS の Checkbox は入力欄の名前を見える文字 (label) から取る (aria-labelledby が aria-label に
              勝つ)。 見える「すべて」は読み上げから外し、 読み上げは群の名前を含む文を見えない文字で持つ */}
          <Checkbox checked={parent.checked} onCheckedChange={parent.onToggle}>
            <VisuallyHidden>
              {t('page:balanceDemand.filter.groupAll', {
                group: t(`page:balanceDemand.filter.${parent.group}`),
              })}
            </VisuallyHidden>
            <span aria-hidden="true">{t('common:filter.all')}</span>
          </Checkbox>
          <HelpIcon
            ariaLabel={t('page:balanceDemand.filter.groupHelp', {
              group: t(`page:balanceDemand.filter.${parent.group}`),
            })}
            content={t(`page:balanceDemand.filter.${parent.group}Tooltip`)}
          />
        </Flex>
      )}
      {items.map((item) =>
        renderToggle(
          item,
          (parent !== undefined && !parent.checked) || isItemDisabled(item),
        ),
      )}
      {trailing}
    </Flex>
  )

  const items: FilterGridItem[] = [
    {
      key: 'date',
      label: dateLabel,
      control: (
        <DisplayDateControl
          displayDate={displayDate}
          labelKeyPrefix="page:balanceDemandDetail.filter"
          onDateChange={onDateChange}
          onToday={onToday}
        />
      ),
    },
    {
      key: 'bg',
      label: bgLabel,
      // 幅 200px は BG の取り違えを避けるため小売より広く取った値で、収まらない
      // BG 名は省略して全文を Tooltip と開いた一覧で読む (#9882 Des 判断)。
      // 素の DS Select は menu を親の中に描くため、直下の表の sticky header が
      // menu の上に重なって選択肢を押せない (F6 で実測)。 body へ portal する
      // DsSelect を使う。
      // 「すべて」の間は Tooltip を外さず閉じたままにする。HintTooltip のように
      // 外すと Select が remount され、選んだ直後に focus が外れる。
      control: (
        <Tooltip
          content={selectedBgLabel}
          open={selectedBgLabel === undefined ? false : undefined}
        >
          <Box>
            <DsSelect
              width="200px"
              aria-label={bgLabel}
              onChange={handleBgChange}
              options={bgSelectOptions}
              value={bgFilter.selectedCode}
            />
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'retail',
      label: retailLabel,
      // 無効の理由は「BG を選ぶと有効になる」で未実装ではないので準備中ラベルは出さない。
      control: (
        <DsSelect
          width="160px"
          aria-label={retailLabel}
          isDisabled={!bgFilter.isRetailerSelectEnabled}
          onChange={handleRetailChange}
          options={retailOptions}
          value={bgFilter.selectedRetailerCode}
        />
      ),
    },
    {
      key: 'demandGroup',
      label: demandGroupLabel,
      // 絞り込みがまだ効かない (read model に需要グループの集計軸が無い。#2002)。操作できるのに
      // 何も起きない状態は「絞り込んだつもり」の誤読を生むので、 Select は disabled にして
      // 理由を短く添える。
      control: (
        <Flex alignItems="center" gap="4px">
          <DsSelect
            width="160px"
            aria-label={demandGroupLabel}
            isDisabled
            onChange={handleDemandGroupChange}
            options={demandGroupOptions}
            value={filter.demandGroup}
          />
          <Text as="span" textStyle="caption" color="Text.lowEmphasis">
            {t('page:balanceDemandDetail.filter.comingSoon')}
          </Text>
        </Flex>
      ),
    },
    {
      key: 'series-basic',
      label: t('page:balanceDemand.filter.basicSeries'),
      control: renderGroup(
        BASIC_TOGGLES,
        (item) => item.key === 'demandPlan' && !bgFilter.isDemandPlanEnabled,
      ),
    },
    {
      key: 'series-voltage',
      label: t('page:balanceDemand.filter.voltageClass'),
      control: renderGroup(VOLTAGE_TOGGLES, () => false, {
        group: 'voltageClass',
        checked: filter.voltageEnabled,
        onToggle: () =>
          onFilterChange({
            ...filter,
            voltageEnabled: !filter.voltageEnabled,
          }),
      }),
    },
    {
      key: 'series-analysis',
      label: t('page:balanceDemand.filter.analysis'),
      control: renderGroup(
        ANALYSIS_TOGGLES,
        (item) =>
          item.key === 'procurementNet' && !bgFilter.isProcurementNetEnabled,
        {
          group: 'analysis',
          checked: filter.analysisEnabled,
          onToggle: () =>
            onFilterChange({
              ...filter,
              analysisEnabled: !filter.analysisEnabled,
            }),
        },
      ),
    },
    {
      key: 'series-detail',
      label: t('page:balanceDemand.filter.detail'),
      control: renderGroup(
        DETAIL_TOGGLES,
        () => false,
        {
          group: 'detail',
          checked: filter.detailEnabled,
          onToggle: () =>
            onFilterChange({
              ...filter,
              detailEnabled: !filter.detailEnabled,
            }),
        },
        hasAwaitingVoltageKoma && <AwaitingVoltageLegend />,
      ),
    },
  ]

  return (
    // design `css/balance.css:10` `.filter-card { margin-bottom:2px }`
    <Box flexShrink={0} marginBottom="2px">
      <CollapsibleFilterToolbar
        modifiedCount={modifiedCount}
        onClear={clearFilters}
        storageKey={BALANCE_DEMAND_DETAIL_FILTER_STORAGE_KEY}
        summary={summary}
      >
        <FilterGrid items={items} />
      </CollapsibleFilterToolbar>
    </Box>
  )
}
